import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Boxes, CircleAlert } from 'lucide-react'
import { auth } from '@/api/endpoints'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Err } from '@/components/Err'
import { EMAIL_DOMAINS, EMAIL_DOMAIN_CUSTOM, LOGIN_NAME_HINT, LOGIN_NAME_RULE, MAIL_UNREACHABLE_WORD, joinEmail, loginNameOk, loginNameTyped, mailUnreachable } from '@/domain/signup'

// 계정 신청(F-14). 이름 · 이메일 · 아이디와 비밀번호를 함께 받는다(F-120) — 메일의 링크는 이메일 확인만 하고,
// 관리자가 승인하면 여기서 정한 비밀번호로 로그인한다.
// 로그인 화면의 `계정 신청` 에서 오고, 끝나면 로그인으로 돌아간다.
// 이메일은 앞부분을 적고 도메인을 고른다(F-75). `직접 입력` 이면 주소 전체를 적고, 보내는 값은 늘 합친 주소 하나다.

const SELECT = 'h-9 shrink-0 rounded-md border border-input bg-background px-2 text-base sm:text-[13px]'

export default function Signup() {
  const [name, setName] = useState('')
  const [local, setLocal] = useState('')
  const [domain, setDomain] = useState<string>(EMAIL_DOMAINS[0] ?? EMAIL_DOMAIN_CUSTOM)
  const [typed, setTyped] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [params] = useSearchParams()
  // OAuth 로 처음 온 사람은 신청이 이미 만들어져 여기로 온다(F-34).
  const [done, setDone] = useState(params.get('pending') === '1')
  const viaOAuth = params.get('pending') === '1'
  // 신청의 전부가 인증 메일 한 통에 달려 있으므로, 그 메일이 닿지 않는 주소는
  // 적는 동안 말해 준다 — 신청하고 기다린 뒤에 알게 되는 것이 가장 나쁘다.
  const email = joinEmail(local, domain, typed)
  const unreachable = mailUnreachable(email)
  const custom = domain === EMAIL_DOMAIN_CUSTOM
  // 걸러 낸 뒤에 남는 어김은 첫 글자뿐이다 — 비운 칸은 아직 어긴 것이 아니다.
  const idBad = username !== '' && !loginNameOk(username)
  const short = password !== '' && password.length < 8
  const differ = again !== '' && again !== password

  // 앞부분 칸에 `@` 를 치면 주소 전체를 적는 것이다 — 직접 입력으로 넘긴다.
  const onLocal = (v: string) => {
    if (v.includes('@')) {
      setDomain(EMAIL_DOMAIN_CUSTOM)
      setTyped(v)
      return
    }
    setLocal(v)
  }
  const onDomain = (d: string) => {
    if (d === EMAIL_DOMAIN_CUSTOM) setTyped(email)
    else if (custom) setLocal(typed.split('@')[0] ?? '')
    setDomain(d)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await auth.register(username.trim(), name.trim(), email, password)
      setPassword('')
      setAgain('')
      setDone(true)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid h-full place-items-center">
      <form onSubmit={submit} className="grid w-full max-w-80 gap-3 px-4">
        <div className="mb-2 flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-lg bg-brand text-white">
            <Boxes className="size-4.5" />
          </span>
          <span className="grid leading-tight">
            <span className="font-semibold tracking-tight">계정 신청</span>
            <span className="text-[11px] text-muted-foreground">Leorca Cluster Control System</span>
          </span>
        </div>
        {done ? (
          <>
            <p className="text-[13px]">{viaOAuth ? '신청을 받았습니다. 관리자가 승인하면 메일로 알려 드립니다.' : '신청을 받았습니다. 메일의 링크로 이메일을 확인하세요.'}</p>
            <Button asChild variant="outline"><Link to="/">로그인</Link></Button>
          </>
        ) : (
          <>
            <Input placeholder="이름" autoComplete="name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
            <div className="flex items-center gap-1.5">
              {custom ? (
                // 메일이 닿지 않는 도메인은 고르는 목록에 없다 — 경고는 직접 입력에서만 선다.
                <div className="relative min-w-0 flex-1">
                  <Input
                    type="email" placeholder="이메일" autoComplete="email" maxLength={254} aria-label="이메일"
                    value={typed} onChange={(e) => setTyped(e.target.value)}
                    aria-invalid={unreachable || undefined}
                    className={unreachable ? 'border-danger/45 pr-9 focus-visible:border-danger/60 focus-visible:ring-danger/20' : undefined}
                  />
                  {unreachable ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span role="img" aria-label={MAIL_UNREACHABLE_WORD}
                          className="absolute inset-y-0 right-2.5 inline-flex items-center text-danger-ink">
                          <CircleAlert className="size-4" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">{MAIL_UNREACHABLE_WORD}</TooltipContent>
                    </Tooltip>
                  ) : null}
                </div>
              ) : (
                <>
                  <Input
                    placeholder="이메일" autoComplete="email" maxLength={64} aria-label="이메일 앞부분"
                    value={local} onChange={(e) => onLocal(e.target.value)} className="min-w-0 flex-1"
                  />
                  <span className="text-muted-foreground">@</span>
                </>
              )}
              <select aria-label="이메일 도메인" className={SELECT} value={domain} onChange={(e) => onDomain(e.target.value)}>
                {EMAIL_DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
                <option value={EMAIL_DOMAIN_CUSTOM}>직접 입력</option>
              </select>
            </div>
            <div className="grid gap-1.5">
              {/* 규칙은 치는 동안 그 자리에서 말한다 — 신청을 눌러 거절로 알게 되면 같은 일을 되풀이한다. */}
              <Input
                placeholder="아이디" autoComplete="username" maxLength={32}
                autoCapitalize="off" autoCorrect="off" spellCheck={false}
                value={username} onChange={(e) => setUsername(loginNameTyped(e.target.value))}
                aria-invalid={idBad || undefined}
              />
              <span className={idBad ? 'text-[11px] text-danger-ink' : 'text-[11px] text-muted-foreground'}>
                {idBad ? LOGIN_NAME_RULE : LOGIN_NAME_HINT}
              </span>
            </div>
            <div className="grid gap-1.5">
              <Input type="password" placeholder="비밀번호" autoComplete="new-password" maxLength={256} value={password}
                onChange={(e) => setPassword(e.target.value)} aria-invalid={short || undefined} />
              <Input type="password" placeholder="비밀번호 확인" autoComplete="new-password" maxLength={256} value={again}
                onChange={(e) => setAgain(e.target.value)} aria-invalid={differ || undefined} />
              {short || differ ? (
                <span className="text-[11px] text-danger-ink">{short ? '비밀번호는 8자 이상이어야 합니다.' : '비밀번호가 서로 다릅니다.'}</span>
              ) : null}
            </div>
            <Button type="submit" disabled={busy || unreachable || !name.trim() || !email || !loginNameOk(username) || password.length < 8 || again !== password} className="bg-brand hover:bg-brand-hover">
              {busy ? '신청 중' : '신청'}
            </Button>
            <Err error={error} />
            <Link to="/" className="text-center text-[12px] text-muted-foreground hover:text-foreground hover:underline">로그인</Link>
          </>
        )}
      </form>
    </div>
  )
}
